import { useState } from 'react';
import {
  Text,
  Card,
  VStack,
  HStack,
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  useDisclosure,
} from '@wegroup/design-system';
import { AnvaQuote } from '@flows/src/types/anva';
import { toEuro } from '@sales-conversation/src/utils/stringUtils';
import useAnvaFlowProductsBasketStore from '@flows/src/store/zustand/flows-products/useAnvaFlowProductsBasketStore';
import { useTranslation } from 'react-i18next';
import { isMaatwerkQuote } from '@flows/src/utils/basketCalculations';
import AnvaAddToPackageModal from '@flows/src/components/flow-products/quotes/basket/AnvaAddToPackageModal';
import AnvaQuoteEditModal from '@flows/src/components/flow-products/quotes/basket/AnvaQuoteEditModal';
import cogoToast from 'cogo-toast';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import { useAnvaProducts } from '@flows/src/api/anva-flows/getAnvaProducts';
import { PartyType } from '@parties/src/types';
import { useBasketCalculation } from '@flows/src/components/flow-products/quotes/basket/AnvaBasketCalculationProvider';
import { buildCalculationPayload } from '@flows/src/utils/basketCalculationPayload';

interface FormValues {
  start_date?: string;
  payment_interval: string | null;
  anva_collection_method_code: number | null | undefined;
}

interface Props {
  data: AnvaQuote;
}

const AnvaFlowProductsSideBarBasketItem = ({ data }: Props) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();

  // Get session data to determine party type for product catalogue lookup
  const queryparams = useQueryParams();
  const sessionId = queryparams.get('session_id') || '';
  const { data: sessionData } = useFlowsSession(sessionId || '');
  const partyType = (sessionData?.prospect?.type || 'CUSTOMER') as PartyType;

  // Get product catalogue to check product configuration
  const { data: productCatalogue } = useAnvaProducts(partyType);

  // Find the product configuration for this quote
  const productConfig = productCatalogue?.items?.find(
    (product) => product.id === data.anva_product_id,
  );

  // Check if this product can be closed as a loose policy
  const canBeClosedAsLoosePolicy =
    productConfig?.can_be_closed_as_loose_policy ?? true;

  // Determine if only one option is available
  const canBeAddedAsPackage = data.possible_packages.length > 0;
  const canBeAddedAsLoose = canBeClosedAsLoosePolicy;

  const onlyPackageAvailable = canBeAddedAsPackage && !canBeAddedAsLoose;
  const onlyLooseAvailable = canBeAddedAsLoose && !canBeAddedAsPackage;
  const hasMultipleOptions = canBeAddedAsPackage && canBeAddedAsLoose;

  // Store for edit modal state
  const [editModalState, setEditModalState] = useState<{
    mode: 'insurance' | 'package';
    packageCode?: string;
  }>({ mode: 'insurance' });

  // Import necessary functions from the basket store
  const addToInsurances = useAnvaFlowProductsBasketStore(
    (state) => state.addToInsurances,
  );
  const addToPackage = useAnvaFlowProductsBasketStore(
    (state) => state.addToPackage,
  );
  const createPackage = useAnvaFlowProductsBasketStore(
    (state) => state.createPackage,
  );
  const updatePackage = useAnvaFlowProductsBasketStore(
    (state) => state.updatePackage,
  );
  const packages = useAnvaFlowProductsBasketStore((state) => state.packages);

  // Handler for adding as package - now checks if package exists and has collection method
  const handleAddToPackage = (packageCode: string) => {
    // Find the package template from possible packages
    const anvaPackage = data.possible_packages.find(
      (pkg) => pkg.code === packageCode,
    );
    if (!anvaPackage) return;

    // Check if package already exists and has collection method
    const existingPackage = packages.find((pkg) => pkg.code === packageCode);

    if (!existingPackage || !existingPackage.anva_collection_method_code) {
      // Package doesn't exist or lacks incassowijze - show modal
      createPackage(anvaPackage);

      // Set edit modal state for package mode and open it
      setEditModalState({ mode: 'package', packageCode });
      onClose(); // Close package selection modal
      onEditModalOpen(); // Open edit modal
    } else {
      // Package exists and has incassowijze - direct add without modal
      const updatedQuoteData: AnvaQuote = {
        ...data,
        // Inherit package-level settings if quote doesn't have them
        start_date: data.start_date || existingPackage.start_date,
        payment_interval:
          data.payment_interval || existingPackage.payment_interval || null,
        anva_collection_method_code:
          data.anva_collection_method_code ||
          existingPackage.anva_collection_method_code,
      };

      addToPackage(packageCode, updatedQuoteData);
      onClose(); // Close package selection modal
      cogoToast.success(t('FLOWS_PRODUCT_ADDED_TO_PACKAGE'));
    }
  };

  // Handler for adding as loose insurance - now opens edit modal first
  const handleAddToInsurance = () => {
    // Set edit modal state for insurance mode and open it
    setEditModalState({ mode: 'insurance' });
    onEditModalOpen();
  };

  // Handler for package selection - auto-select if only one option
  const handlePackageSelection = () => {
    if (data.possible_packages.length === 1) {
      // Auto-select the single package option
      handleAddToPackage(data.possible_packages[0].code);
    } else {
      // Show modal for multiple package options
      onOpen();
    }
  };

  // Handler for automatic selection when only one option is available
  const handleAutomaticSelection = () => {
    if (onlyPackageAvailable) {
      // Only package available - auto-select if single package, otherwise show modal
      if (data.possible_packages.length === 1) {
        handleAddToPackage(data.possible_packages[0].code);
      } else {
        onOpen();
      }
    } else if (onlyLooseAvailable) {
      // Only loose insurance available
      handleAddToInsurance();
    }
  };

  // Handler for confirming add after payment method is filled
  const { calculate, hasActiveJobs } = useBasketCalculation();

  const handleConfirmAdd = async (formValues: FormValues) => {
    const updatedQuoteData = {
      ...data,
      start_date: formValues.start_date,
      payment_interval: formValues.payment_interval,
      anva_collection_method_code: formValues.anva_collection_method_code,
    };

    if (editModalState.mode === 'package' && editModalState.packageCode) {
      // Add to the selected package
      addToPackage(editModalState.packageCode, updatedQuoteData);

      // Update the package with collection method and other package-level data
      updatePackage(editModalState.packageCode, {
        anva_collection_method_code:
          formValues.anva_collection_method_code ?? undefined,
        start_date: formValues.start_date,
        payment_interval: formValues.payment_interval ?? undefined,
      });

      cogoToast.success(t('FLOWS_PRODUCT_ADDED_TO_PACKAGE'));
    } else {
      // Add as loose insurance
      addToInsurances(updatedQuoteData);
      cogoToast.success(t('FLOWS_PRODUCT_ADDED_AS_INSURANCE'));
    }

    onEditModalClose();

    const paymentIntervalChanged =
      updatedQuoteData.payment_interval !== data.payment_interval;

    if (paymentIntervalChanged) {
      try {
        const calculationPayload = buildCalculationPayload(
          [],
          [updatedQuoteData],
        );
        await calculate(calculationPayload);
      } catch (calcError) {
        console.error('Calculation error after add:', calcError);
      }
    }
  };

  return (
    <>
      <AnvaAddToPackageModal
        isOpen={isOpen}
        onClose={onClose}
        packageOptions={data.possible_packages}
        onAddToPackage={handleAddToPackage}
      />
      <AnvaQuoteEditModal
        isOpen={isEditModalOpen}
        onClose={onEditModalClose}
        quoteData={data}
        packageCode={editModalState.packageCode}
        isAddingMode={true}
        onConfirmAdd={handleConfirmAdd}
      />
      <Card boxShadow="none">
        <HStack justify="flex-start">
          <VStack align="stretch" spacing="0" flexGrow={1}>
            <Text textStyle="smallBody" color="gray.300">
              {data.insurance_company.name}
            </Text>
            <Text textStyle="normalBodyMedium">{data.name}</Text>
          </VStack>
          <Text color="gray.500">
            {isMaatwerkQuote(data)
              ? t('MAATWERK')
              : toEuro(data?.total_premium)}
          </Text>
        </HStack>
        {hasMultipleOptions ? (
          <Menu placement="right-start">
            <MenuButton
              as={Button}
              ml="auto"
              mt="2"
              size="sm"
              variant="secondary"
              rightIcon={<Icon name="GeneralActionsChevronRight" boxSize="5" />}
            >
              Verzekeren als
            </MenuButton>
            <Portal>
              <MenuList>
                <MenuItem
                  icon={<Icon name="GeneralOthersBox" />}
                  sx={{ svg: { scale: 0.9, transform: 'auto' } }}
                  onClick={handlePackageSelection}
                  isDisabled={
                    data.possible_packages.length === 0 || hasActiveJobs
                  }
                >
                  Pakket
                </MenuItem>
                <MenuItem
                  icon={<Icon name="GeneralNavigationShoppingBag" />}
                  onClick={handleAddToInsurance}
                  isDisabled={!canBeClosedAsLoosePolicy || hasActiveJobs}
                >
                  Losse verzekering
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        ) : (
          <Button
            ml="auto"
            mt="2"
            size="sm"
            variant="secondary"
            onClick={handleAutomaticSelection}
            isDisabled={hasActiveJobs}
            rightIcon={
              onlyPackageAvailable ? (
                <Icon name="GeneralOthersBox" boxSize="5" />
              ) : (
                <Icon name="GeneralNavigationShoppingBag" boxSize="5" />
              )
            }
          >
            {onlyPackageAvailable
              ? 'Toevoegen aan pakket'
              : 'Toevoegen als losse verzekering'}
          </Button>
        )}
      </Card>
    </>
  );
};

export default AnvaFlowProductsSideBarBasketItem;
